import React, { useState, FC, useEffect, ReactNode, useMemo } from "react"
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelector, useDispatch } from 'react-redux';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Stack, Container, Divider, Paper, Chip, IconButton, Checkbox, FormControlLabel } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { useAuth } from "react-oidc-context";
import { cognitoAuthConfig, Condition, condition, coonditionContext } from '../..';
import FacebookIcon from '@mui/icons-material/Facebook';
import AppleIcon from '@mui/icons-material/Apple';
import { ReactComponent as GoogleLogo } from '../../assets/google-logo.svg';
import EmailIcon from '@mui/icons-material/Email';
import { CircularProgressCentered, HeaderContent } from '../../app/components/sharedcomponents';
import { updateUrlParams, SmsLogin } from "./smsLogin";
import CloseIcon from '@mui/icons-material/Close';
import { useAnalyzeTinyTokenQuery, useLazyAnalyzeTinyTokenQuery, useLazyVerifyTinyTokenQuery } from "../../features/oidcSlice";
import { useAuthProfile } from "../../app/hooks";
import { formatPhoneNumber } from "../../app/utils";
import { skipToken } from "@reduxjs/toolkit/query";
interface SignInUIProps {
    deleteProfile?: boolean
    children?: ReactNode;
    isSignUp?: boolean
    noLogo?: boolean,
    noBorder?: boolean
    navigateTo?: string
    showSMSLoginAtStart?: boolean
}
// const MAGIC_TOKEN_OK = "MAGIC_TOKEN_OK"
// const MAGIC_TOKEN_ERROR = "MAGIC_TOKEN_ERROR"
// const MAGIC_TOKEN_EXPIRED = "MAGIC_TOKEN_EXPIRED"
// const MAGIC_TOKEN_PENDING = "MAGIC_TOKEN_PENDING"
// const MAGIC_TOKEN_NOT_AVAILABLE = "MAGIC_TOKEN_NOT_AVAILABLE"
// const MAGIC_TOKEN_LOGIN_FAILURE = "MAGIC_TOKEN_LOGIN_FAILURE"
export const MainSignIn: FC<SignInUIProps> = ({ children, isSignUp, noLogo, noBorder, deleteProfile = false, navigateTo, showSMSLoginAtStart }) => {
    //const auth = useAuth()
    const { authStatus, profile: loggedInUserProfile, isLoading: isLoadingLoggedInUserProfile, isSuccess: isSuccessLoggedInUserProfile, isError: isErrorLoggedInUserProfile, error: errorLoggedInUserProfile, customAuth, auth } = useAuthProfile()
    const [verifyTinyToken, { isLoading: verifyTinyTokenIsLoading, isSuccess: verifyTinyTokenIsSuccess, error: verifyTinyTokenError }] = useLazyVerifyTinyTokenQuery();
    //const [analyzeTinyToken, { isLoading: analyzeTinyTokenIsLoading, isSuccess: analyzeTinyTokenIsSuccess, error: analyzeTinyTokenError }] = useLazyAnalyzeTinyTokenQuery();

    const [phoneLinkChecked, setPhoneLinkChecked] = useState<boolean>(true);
    const navigate = useNavigate()
    const location = useLocation();
    const { condition, baseName } = React.useContext<Condition>(coonditionContext);
    // Round-trip the BCAN-study flag through the OIDC `state` so it survives the federated login redirect (which wipes
    // in-memory Redux). Re-hydrated in index.tsx onSigninCallback. Source: dialog.bcanStudy, set on the /bcanstudy landing.
    const bcanStudy = useSelector((s: any) => s.dialog.bcanStudy)
    // Same mechanism for Research Connect Direct: source dialog.researchConnect, set on the /researchconnect landing.
    const researchConnect = useSelector((s: any) => s.dialog.researchConnect)
    const label = { slotProps: { input: { 'aria-label': 'Checkbox demo' } } };

    //where user is trying to go if coming from a different page
    const from = (location.state as { from?: Location })?.from?.pathname || "/";
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const code = params.get("code")
    const [showSMSLogin, setShowSMSLogin] = useState((!!code && code === 'true') || showSMSLoginAtStart)
    const t = params.get("t");
    const redirect = params.get("redirect")
    const arg = t ?? skipToken
    const { data: tokenData, isLoading: analyzeTinyTokenIsLoading, isSuccess: analyzeTinyTokenIsSuccess, error: analyzeTinyTokenError } = useAnalyzeTinyTokenQuery(arg);

    const disabled = isLoadingLoggedInUserProfile || analyzeTinyTokenIsLoading
    return (
        <Container
            sx={{
                mt: 'auto',  // margin-top: auto
                mb: 'auto',  // margin-bottom: auto
                width: '100%',
                display: 'block', // can also omit
            }}
        >
            <Paper elevation={0} sx={{
                mt: 0, pt: 3, pb: 3, pl: { xs: 2, sm: 5 }, pr: { xs: 2, sm: 5 },
                maxWidth: 600,
                mx: 'auto',
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.55)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: '1px solid rgba(164, 142, 233, 0.18)',
                boxShadow: '0 8px 32px rgba(101, 110, 255, 0.10)',
            }}>
                {/* <IconButton onClick={() => navigate("/")} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, }}                >
                    <CloseIcon />
                </IconButton> */}
                {(isLoadingLoggedInUserProfile && analyzeTinyTokenIsLoading) && <CircularProgressCentered />}
                <Stack
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        justifyItems: 'center',
                        //backgroundColor: "green",
                        gap: 2
                    }}

                >
                    {!noLogo && <HeaderContent />}
                    {children}
                    {/* BCAN-study: land custom/SMS login on /bcanstudy too (parity with the federated `route` override) so
                        the URL — hence banner + consent gate — is reload-resilient for either method. */}
                    {showSMSLogin && <><SmsLogin showExistingAccountNotice={!!!code} navigateTo={bcanStudy ? '/bcanstudy' : researchConnect ? '/researchconnect' : (navigateTo ?? redirect ?? '')} lockedPhoneNumber={tokenData?.magicUserId} />
                        <Button sx={{ fontSize: 12 }}
                            disabled={disabled}
                            onClick={
                                () => setShowSMSLogin(false)} disableElevation startIcon={<ArrowBackIcon />}>
                            Sign-in Options
                        </Button></>}
                    {!showSMSLogin && <>
                        <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
                            Log in or sign up
                        </Typography>

                        <Stack sx={{ width: '100%', gap: 1.5 }}>
                            {/* Continue with phone */}
                            <Button
                                variant="outlined"
                                fullWidth
                                disableElevation
                                startIcon={<SmartphoneIcon sx={{ fontSize: '1.2rem' }} />}
                                disabled={disabled || tokenData?.congitoMatch?.isCongnitoMatchedByEmail}
                                onClick={async () => {
                                    if (analyzeTinyTokenIsSuccess && !tokenData.isExpired) {
                                        try {
                                            const tokenDataVerified = await verifyTinyToken({ t: t!, signInMode: 'user' }).unwrap();
                                            console.log("Magic token verified, cookies set");
                                            const { sub, profile, expires_at } = tokenData;
                                            auth.removeUser()
                                            const navigateAfterLogin = updateUrlParams({
                                                url: navigateTo ?? redirect ?? '',
                                                removeParams: ["t", "userSignIn"],
                                            }) || '/'
                                            customAuth.login(tokenDataVerified)
                                            navigate(navigateAfterLogin)
                                        } catch (err) {
                                            console.warn("Magic token login error", err);
                                            setShowSMSLogin(true)
                                        }
                                    } else
                                        setShowSMSLogin(true)
                                }}
                                sx={{
                                    textTransform: 'none', fontSize: '0.95rem', fontWeight: 500,
                                    borderRadius: '50px', py: 1.25,
                                    borderColor: 'rgba(0,0,0,0.2)', color: 'text.primary',
                                    '&:hover': { borderColor: 'rgba(0,0,0,0.4)', backgroundColor: 'rgba(0,0,0,0.02)' },
                                }}
                            >
                                Continue with phone{(analyzeTinyTokenIsSuccess && tokenData?.isMagicUserIdPhone) && ` ${formatPhoneNumber(tokenData.magicUserId)}`}
                            </Button>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#808080', lineHeight: 1.2, fontWeight: 400, textAlign: 'center', mt: -0.75 }}>
                                Unlock Text Assistant on your phone
                            </Typography>

                            {/* Continue with socials */}
                            <Button
                                variant="outlined"
                                fullWidth
                                disabled={disabled || tokenData?.congitoMatch?.isCongnitoMatchedByPhone}
                                disableElevation
                                startIcon={<Stack direction="row" gap={0.75}><FacebookIcon sx={{ fontSize: '1.2rem', color: '#1877F2' }} /><GoogleLogo style={{ width: 18, height: 18 }} /><AppleIcon sx={{ fontSize: '1.2rem', color: '#000000' }} /></Stack>}
                                onClick={() => {
                                    const state = {
                                        baseName,
                                        // BCAN-study / Research-Connect participants: re-route back to the landing route post-login so
                                        // the URL handle re-establishes the consent default (and, for BCAN, the banner suppression) —
                                        // the route IS the gate (no separate flag round-trip). Overrides the normal return route.
                                        ...(bcanStudy ? { route: '/bcanstudy' } : researchConnect ? { route: '/researchconnect' } : (navigateTo || redirect) ? {
                                            route: (navigateTo ? updateUrlParams({
                                                url: navigateTo,
                                                removeParams: ["t", "userSignIn"],
                                                ...(tokenData?.isMagicUserIdPhone && tokenData?.magicUserId && phoneLinkChecked) ?
                                                    { addParams: [{ key: "phone", value: tokenData?.magicUserId }] } : {}
                                            }) : undefined) ?? redirect
                                        } : {})
                                    }
                                    auth.signinRedirect({ state });
                                }}
                                sx={{
                                    textTransform: 'none', fontSize: '0.95rem', fontWeight: 500,
                                    borderRadius: '50px', py: 1.25,
                                    borderColor: 'rgba(0,0,0,0.2)', color: 'text.primary',
                                    '&:hover': { borderColor: 'rgba(0,0,0,0.4)', backgroundColor: 'rgba(0,0,0,0.02)' },
                                }}
                            >
                                Continue with socials
                            </Button>

                            {/* Continue with email */}
                            <Button
                                variant="outlined"
                                fullWidth
                                disabled={disabled || tokenData?.congitoMatch?.isCongnitoMatchedByPhone}
                                disableElevation
                                startIcon={<EmailIcon sx={{ fontSize: '1.2rem' }} />}
                                onClick={() => {
                                    const state = {
                                        baseName,
                                        // BCAN-study / Research-Connect participants: re-route back to the landing route post-login so
                                        // the URL handle re-establishes the consent default (and, for BCAN, the banner suppression) —
                                        // the route IS the gate (no separate flag round-trip). Overrides the normal return route.
                                        ...(bcanStudy ? { route: '/bcanstudy' } : researchConnect ? { route: '/researchconnect' } : (navigateTo || redirect) ? {
                                            route: (navigateTo ? updateUrlParams({
                                                url: navigateTo,
                                                removeParams: ["t", "userSignIn"],
                                                ...(tokenData?.isMagicUserIdPhone && tokenData?.magicUserId && phoneLinkChecked) ?
                                                    { addParams: [{ key: "phone", value: tokenData?.magicUserId }] } : {}
                                            }) : undefined) ?? redirect
                                        } : {})
                                    }
                                    auth.signinRedirect({ state });
                                }}
                                sx={{
                                    textTransform: 'none', fontSize: '0.95rem', fontWeight: 500,
                                    borderRadius: '50px', py: 1.25,
                                    borderColor: 'rgba(0,0,0,0.2)', color: 'text.primary',
                                    '&:hover': { borderColor: 'rgba(0,0,0,0.4)', backgroundColor: 'rgba(0,0,0,0.02)' },
                                }}
                            >
                                Continue with email
                            </Button>
                        </Stack>

                        <Typography variant="caption" sx={{ color: '#808080', textAlign: 'center', maxWidth: 360 }}>
                            By logging in with your phone number, you'll be able to use Polygon Text Assistant to see info on members, resources, education, and more - right on your phone.
                        </Typography>

                        {(analyzeTinyTokenIsSuccess && tokenData?.isMagicUserIdPhone) && (
                            <Stack sx={{ alignItems: 'center' }}>
                                <FormControlLabel label={<Typography variant="body2" sx={{ color: '#808080' }}>{`Link ${formatPhoneNumber(tokenData.magicUserId)} to my account`}</Typography>}
                                    control={<Checkbox
                                        checked={phoneLinkChecked}
                                        onChange={(e) => setPhoneLinkChecked(e.target.checked)}
                                    />}
                                />
                                <Typography variant="caption" sx={{ color: '#808080', textAlign: 'center' }}>Please use this login method to link phone number at least once if you've previously logged in to Patient Connect with email or using Facebook/Google/Apple. Once your phone number is linked to your account you'll be able to log in easily with either your phone or email/Facebook/Google/Apple</Typography>
                            </Stack>
                        )}
                    </>}
                </Stack>
            </Paper>
        </Container >
    )
}